/**
 * Who this build belongs to.
 *
 * <p>Every name, signature and piece of artwork that reaches a bill comes from
 * here. The defaults are WAJID FLOWER's own, so a build with no settings at all
 * is Uzair's shop exactly as it has always been - which is the whole safety
 * argument for doing it this way. Nothing about his bill depends on a setting
 * being remembered.
 *
 * <p>Read at BUILD time rather than fetched from the server. Each shop already
 * needs its own build because its address is baked in; the name never changes
 * in practice; and a name compiled into the bundle cannot arrive late, arrive
 * empty, or fail to arrive at all. A bill that prints with no shop name across
 * the top is the one outcome worth engineering against, and both a runtime
 * fetch and a settings row in the database create a way for it to happen.
 *
 * <p>The artwork is not here: it comes in through the `@shop` alias, which
 * vite.config.ts points at one shop's asset folder. That way a build carries
 * its own shop's logo and nobody else's.
 */

/** A setting, or the shop this app was written for. */
function setting(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

export const shop = {
  /** Across the top bar, and signing off every WhatsApp message. */
  name: setting(import.meta.env.VITE_SHOP_NAME, 'WAJID FLOWER'),

  /** Leads every downloaded file, so two shops' files never share a name. */
  slug: setting(import.meta.env.VITE_SHOP_SLUG, 'wajid-flower'),

  /**
   * The name already filled in on the login screen.
   *
   * <p>Must match the owner account the server creates - APP_OWNER_USERNAME on
   * the backend. It was written in as "uzair", which had the second shop's owner
   * looking at somebody else's name on his own login screen.
   */
  login: setting(import.meta.env.VITE_SHOP_LOGIN, 'uzair'),

  /** The line under the lockup on the letterhead. */
  tagline: setting(import.meta.env.VITE_SHOP_TAGLINE, 'پھولوں کی ہول سیل — کراچی'),

  /**
   * The man who signs the bills, in both scripts - the flourish is set in Great
   * Vibes and the line under it is read by the grower. It is the shop owner's
   * own name and not the shop's: a bill already carries the shop's name in gold
   * across the top, and a second copy of it above the signature line reads as
   * two different people signing one sheet.
   */
  signature: setting(import.meta.env.VITE_SHOP_SIGNATURE, 'Uzair'),
  signatureUrdu: setting(import.meta.env.VITE_SHOP_SIGNATURE_UR, 'عزیر'),

  /**
   * How tall the lockup is for its width - 626/760 for Uzair's.
   *
   * <p>Written down rather than measured, because the image has not loaded at
   * the moment the space for it has to be reserved. Get it wrong and the bill
   * either jumps as the logo arrives or prints the shop's name squashed.
   */
  lockupRatio: Number(import.meta.env.VITE_SHOP_LOCKUP_RATIO) || 626 / 760,

  /**
   * Which way round the letterhead runs.
   *
   * <p>`dark` is gold lettering on the near-black plate - right when the logo is
   * made of light strokes. `light` keeps the plate white, which is the only
   * readable choice for a logo drawn in dark ink, and costs less to print.
   */
  letterhead: import.meta.env.VITE_SHOP_LETTERHEAD === 'light' ? 'light' : 'dark',
} as const
